import React, { createContext, useContext, useState, useEffect } from 'react';
import { Formation } from '../types/api';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface FormationsContextType {
  formations: Formation[];
  loading: boolean;
  /** Vertaalsleutel in de `formations`-namespace; de consument roept `t()` aan. */
  errorKey: string | null;
  refreshFormations: () => Promise<void>;
}

const FormationsContext = createContext<FormationsContextType | undefined>(undefined);

export const FormationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentTeam } = useAuth();
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const loadFormations = async (teamId?: number) => {
    try {
      setLoading(true);
      setErrorKey(null);

      if (!teamId) {
        setFormations([]);
        setLoading(false);
        return;
      }
      
      // Check cache first (localStorage) - team-specific cache
      const cacheKey = `formations_cache_team_${teamId}`;
      const timestampKey = `formations_cache_timestamp_team_${teamId}`;
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(timestampKey);
      const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
      
      if (cachedData && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < CACHE_DURATION) {
          setFormations(JSON.parse(cachedData));
          setLoading(false);
          return;
        }
      }
      
      // Fetch from API - team-specific
      const formationsData = await apiService.getFormationsByTeam(teamId);
      setFormations(formationsData);
      
      // Cache the data
      localStorage.setItem(cacheKey, JSON.stringify(formationsData));
      localStorage.setItem(timestampKey, Date.now().toString());
      
    } catch (err) {
      setErrorKey('loadError');
      console.error('Error loading formations:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshFormations = async () => {
    // Clear cache and reload
    if (currentTeam) {
      const cacheKey = `formations_cache_team_${currentTeam.teamId}`;
      const timestampKey = `formations_cache_timestamp_team_${currentTeam.teamId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(timestampKey);
      await loadFormations(currentTeam.teamId);
    }
  };

  useEffect(() => {
    if (currentTeam) {
      loadFormations(currentTeam.teamId);
    } else {
      setFormations([]);
      setLoading(false);
    }
  }, [currentTeam]);

  return (
    <FormationsContext.Provider value={{
      formations,
      loading,
      errorKey,
      refreshFormations
    }}>
      {children}
    </FormationsContext.Provider>
  );
};

export const useFormations = (): FormationsContextType => {
  const context = useContext(FormationsContext);
  if (context === undefined) {
    throw new Error('useFormations must be used within a FormationsProvider');
  }
  return context;
};